import { motion } from "framer-motion"
import { cn } from "@/lib/utils"

interface GlassSwitchProps {
  checked: boolean
  onChange: (next: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
  id?: string
  className?: string
}

export function GlassSwitch({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
  className,
}: GlassSwitchProps) {
  const toggle = () => {
    if (disabled) return
    onChange(!checked)
  }

  const switchButton = (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={toggle}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        checked
          ? "bg-[var(--accent-blue,#2563eb)] border-[var(--accent-blue,#2563eb)]"
          : "bg-glass-card border-glass-border",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <motion.span
        layout
        className={cn(
          "inline-block h-5 w-5 rounded-full bg-white shadow-sm",
          checked ? "ml-auto mr-[2px]" : "ml-[2px] mr-auto",
        )}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        aria-hidden="true"
      />
    </button>
  )

  if (!label && !description) {
    return <div className={className}>{switchButton}</div>
  }

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start justify-between gap-4",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        className,
      )}
    >
      <div className="flex-1 min-w-0">
        {label && (
          <p className="text-sm font-medium text-[var(--text-primary)] leading-tight">
            {label}
          </p>
        )}
        {description && (
          <p className="mt-1 text-xs text-[var(--text-secondary)] leading-snug">
            {description}
          </p>
        )}
      </div>
      {switchButton}
    </label>
  )
}
